export const REGION_LEVEL_TYPE = 1;
export const PROJECT_LEVEL_TYPE = 2;
export const CHILD_LEVEL_TYPE = 3;

export type RowHierarchyKind = 'dri' | 'see';

function compareDriRows(a: any, b: any, ifRegion: boolean): number {
    let prj = 1;
    if (a.project && b.project) {
        prj = (b.project.id < a.project.id ? 1 : 0) - (a.project.id < b.project.id ? 1 : 0);
    }
    if (!a.project && !b.project) {
        prj = 0;
    }
    if (a.project && !b.project) {
        prj = -1;
    }
    if (!a.project && b.project) {
        prj = 1;
    }

    let reg = 1;
    if (a.region && b.region) {
        reg = (parseInt(b.region) < parseInt(a.region) ? 1 : 0) - (parseInt(a.region) < parseInt(b.region) ? 1 : 0);
    }
    if (!a.region && !b.region) {
        reg = 0;
    }
    if (!a.region && b.region) {
        reg = 1;
    }
    if (a.region && !b.region) {
        reg = -1;
    }
    const byType = (b.type < a.type ? 1 : 0) - (a.type < b.type ? 1 : 0);
    return ifRegion ? (reg || prj || byType) : (prj || byType);
}

function compareSeeRows(a: any, b: any, projectField: string): number {
    if (a.region && b.region) {
        if (a.region < b.region) return -1;
        if (a.region > b.region) return 1;
    }
    const aProject = a[a.type === CHILD_LEVEL_TYPE ? projectField : 'project'];
    const bProject = b[b.type === CHILD_LEVEL_TYPE ? projectField : 'project'];
    if (aProject && bProject) {
        if (aProject.id < bProject.id) return -1;
        if (aProject.id > bProject.id) return 1;
    }
    return a.type - b.type;
}

function isProjectAlreadyAdded(kind: RowHierarchyKind, item: any, row: any, ifRegion: boolean, projectField: string): boolean {
    const rowProject = row[projectField];
    if (kind === 'dri') {
        const isSameProjectInRegion = item.region === row.region && item.project && rowProject && item.project.id === rowProject.id;
        const isSameProject = !ifRegion && item.project && rowProject && item.project.id === rowProject.id;
        const isBothNoProject = !ifRegion && item.project === rowProject;
        return !!(isSameProjectInRegion || isSameProject || isBothNoProject);
    }
    if (item.region && row.region) {
        return !!(item.region === row.region && item.project && rowProject && item.project.id === rowProject.id);
    }
    return !!(item.project && rowProject && item.project.id === rowProject.id);
}

export function buildRowHierarchy(rows: any[], kind: RowHierarchyKind, ifRegion: boolean, projectField = 'project'): any[] {
    const regions: any[] = [];
    const projects: any[] = [];
    const records: any[] = [];

    const makeLevelRow = (id: number, region: string, project: any, type: number) => ({
        id,
        region,
        project,
        type,
        open: false,
        collapsed: ifRegion ? type === PROJECT_LEVEL_TYPE : false
    });

    rows.forEach((row: any, i: number) => {
        records.push({
            ...row,
            type: CHILD_LEVEL_TYPE,
            open: false,
            collapsed: true
        });

        if (!ifRegion || !regions.some((item: any) => item.region === row.region)) {
            regions.push(makeLevelRow(i - 10000000, row.region, { id: i - 10000 }, REGION_LEVEL_TYPE));
        }

        if (!projects.some((item: any) => isProjectAlreadyAdded(kind, item, row, ifRegion, projectField))) {
            projects.push(makeLevelRow(i - 10000, row.region, row[projectField], PROJECT_LEVEL_TYPE));
        }
    });

    const hierarchy = [...regions, ...projects, ...records];
    hierarchy.sort((a: any, b: any) => (
        kind === 'dri' ? compareDriRows(a, b, ifRegion) : compareSeeRows(a, b, projectField)
    ));
    assignRowNumbers(hierarchy, ifRegion);
    return hierarchy;
}

export function assignRowNumbers(hierarchy: any[], ifRegion: boolean): void {
    let firstLvl = 0;
    let secondLvl = 0;
    let thirdLvl = 0;
    hierarchy.forEach((row: any) => {
        switch (row.type) {
            case REGION_LEVEL_TYPE:
                if (ifRegion) {
                    ++firstLvl;
                    row.rowNumber = `${firstLvl}`;
                    secondLvl = 0;
                }
                break;
            case PROJECT_LEVEL_TYPE:
                ++secondLvl;
                row.rowNumber = ifRegion ? `${firstLvl}.${secondLvl}` : `${secondLvl}`;
                thirdLvl = 0;
                break;
            case CHILD_LEVEL_TYPE:
                ++thirdLvl;
                row.rowNumber = ifRegion ? `${firstLvl}.${secondLvl}.${thirdLvl}` : `${secondLvl}.${thirdLvl}`;
                break;
            default:
                break;
        }
    });
}

export function collectRowNumbers(hierarchy: any[], keyField: string): Map<any, string> {
    const numbers = new Map<any, string>();
    hierarchy.forEach((row: any) => {
        if (row.type === CHILD_LEVEL_TYPE && row.rowNumber !== undefined) {
            numbers.set(row[keyField], row.rowNumber);
        }
    });
    return numbers;
}

export function mergeSeeWithDri(
    seeRows: any[],
    driRows: any[],
    isSameGroup: (item: any, row: any) => boolean,
    createPlaceholder: (dri: any) => any
): any[] {
    const merged = [...seeRows];
    driRows.forEach((dri: any) => {
        if (merged.findIndex((item: any) => isSameGroup(item, dri)) === -1) {
            merged.push(createPlaceholder(dri));
        }
    });
    return merged.filter((see: any) => driRows.findIndex((item: any) => isSameGroup(item, see)) !== -1);
}

export function sortDriRowsByRegionAndId(driRows: any[]): any[] {
    return driRows.sort((a: any, b: any) => (
        (parseInt(b.region) < parseInt(a.region) ? 1 : 0) - (parseInt(a.region) < parseInt(b.region) ? 1 : 0)
        || (b.id < a.id ? 1 : 0) - (a.id < b.id ? 1 : 0)
    ));
}
